import type { AuthSessionDto, AuthUserDto, AuthAccountDto } from '../types/auth'
import type { User, Account, AccountStatus } from '../types'

/**
 * Нормализация auth response (DTO) → Store shape (User, Account).
 *
 * Зачем: backend pg19back присылает разные shapes из разных endpoints
 * (login, register, telegram-complete, call-complete, session). Поля
 * разной нуллабельности, contractNumber иногда string иногда number,
 * status — unconstrained string. Без normalize в callsite приходится
 * писать `as any` касты — что и было причиной багов в LoginPage:51,60
 * и ContractPage:65.
 *
 * После normalize:
 * - все nullable string user fields → '' (пустая строка)
 * - contractNumber всегда number (формат formatContractNumber требует number)
 * - status принудительно одно из 'active' | 'blocked' (default 'active')
 * - startDate всегда string (default '' если null)
 * - addresses preserved если есть, иначе ['address'] если есть, иначе []
 *
 * Если backend пришлёт shape которого нет в AuthSessionDto — TypeScript
 * скажет на этапе compile, а не в проде через `as any`.
 */

const DEFAULT_USER: Omit<User, 'id'> = {
  firstName: '',
  lastName: '',
  middleName: '',
  phone: '',
  email: '',
  telegram: '',
  telegramId: null,
  vkId: '',
  max: '',
  maxId: null,
  avatar: null,
  birthDate: null,
  nickname: null,
}

function normalizeStatus(raw: string | null | undefined): AccountStatus {
  return raw === 'blocked' ? 'blocked' : 'active'
}

function normalizeContractNumber(raw: number | string | null | undefined): number {
  if (typeof raw === 'number') return raw
  if (typeof raw === 'string') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function normalizeUser(dto: AuthUserDto): User {
  if (!dto.id) {
    throw new Error('normalizeAuthSession: user.id is required')
  }
  return {
    id: dto.id,
    firstName: dto.firstName ?? DEFAULT_USER.firstName,
    lastName: dto.lastName ?? DEFAULT_USER.lastName,
    middleName: dto.middleName ?? DEFAULT_USER.middleName,
    phone: dto.phone ?? DEFAULT_USER.phone,
    email: dto.email ?? DEFAULT_USER.email,
    telegram: dto.telegram ?? DEFAULT_USER.telegram,
    telegramId: dto.telegramId ?? null,
    vkId: dto.vkId ?? DEFAULT_USER.vkId,
    max: dto.max ?? DEFAULT_USER.max,
    maxId: dto.maxId ?? null,
    avatar: dto.avatar ?? null,
    birthDate: dto.birthDate ?? null,
    nickname: dto.nickname ?? null,
    // role: разрешённые значения только 'user'/'admin'/'moderator'.
    // Если backend пришлёт что-то другое — undefined (default to 'user' в UI).
    role: dto.role === 'admin' || dto.role === 'moderator' || dto.role === 'user'
      ? dto.role
      : undefined,
  }
}

function normalizeAccount(dto: AuthAccountDto | null): Account | null {
  if (!dto) return null
  // addresses preferred over single address; deduplicate
  const addressList = dto.addresses && dto.addresses.length > 0
    ? Array.from(new Set(dto.addresses))
    : dto.address
      ? [dto.address]
      : []

  return {
    contractNumber: normalizeContractNumber(dto.contractNumber),
    balance: dto.balance ?? 0,
    status: normalizeStatus(dto.status),
    tariff: dto.tariff ?? '',
    address: dto.address ?? (addressList[0] ?? ''),
    addresses: addressList.length > 0 ? addressList : undefined,
    startDate: dto.startDate ?? '',
    payDay: dto.payDay ?? undefined,
  }
}

/**
 * Главная entry-point. DTO от любого auth endpoint → нормализованный
 * { user, account } готовый к dispatch в Redux store.
 */
export function normalizeAuthSession(dto: AuthSessionDto): { user: User; account: Account | null } {
  return {
    user: normalizeUser(dto.user),
    account: normalizeAccount(dto.account),
  }
}
